import { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl, defineMessages, FormattedMessage } from 'react-intl';

import { useIdentity } from '@/mastodon/identity_context';
import { fetchRelationships, followAccount, unfollowAccount } from 'mastodon/actions/accounts';
import { openModal } from 'mastodon/actions/modal';
import { Button } from 'mastodon/components/button';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';
import { fetchMostSuggestions } from 'mastodon/actions/suggestions';

const messages = defineMessages({
  unfollow: { id: 'account.unfollow', defaultMessage: 'Unfollow' },
  follow_title: {
    id: 'account.follow.title',
    defaultMessage: 'Follow user?',
  },
  follow: { id: 'account.follow', defaultMessage: 'Follow' },
  followBack: { id: 'account.follow_back', defaultMessage: 'Follow back' },
  mutual: { id: 'account.mutual', defaultMessage: 'Mutual' },
  edit_profile: { id: 'account.edit_profile', defaultMessage: 'Edit profile' },
});

const fetchLaoIdToken = async (): Promise<string | null> => {
  try {
    const response = await fetch('/auth/laoid/token');
    if (!response.ok) {
      throw new Error('Failed to fetch token');
    }
    const data = await response.json();
    return data.access_token;
  } catch (error) {
    console.error('Error fetching LaoID token:', error);
    return null;
  }
};

export const FollowButton: React.FC<{
  accountId?: string;
  compact?: boolean;
  onlyFollow?: boolean;
}> = ({ accountId, compact, onlyFollow = false }) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { signedIn } = useIdentity();
  const isProcessingRef = useRef(false);
  const lastEventTypeRef = useRef<string | null>(null);
  const [isActionInProgress, setIsActionInProgress] = useState(false);
  const account = useAppSelector((state) =>
    accountId ? state.accounts.get(accountId) : undefined,
  );
  const relationship = useAppSelector((state) =>
    accountId ? state.relationships.get(accountId) : undefined,
  );
  const following = relationship?.following || relationship?.requested;

  useEffect(() => {
    if (accountId && signedIn) {
      dispatch(fetchRelationships([accountId]));
    }
  }, [dispatch, accountId, signedIn]);

  const handleClick = useCallback(
    async (e: React.TouchEvent<HTMLButtonElement> | React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      if (e.type === 'click' && lastEventTypeRef.current === 'touchstart') {
        return;
      }
      if (isProcessingRef.current) return;

      isProcessingRef.current = true;
      lastEventTypeRef.current = e.type;

      try {
        if (!signedIn) {
          window.location.href = '/auth/laoid';
        } else if (!relationship || !accountId || isActionInProgress) {
          return;
        } else if (accountId === me) {
          await handleOpenProfile();
        } else if (account && (relationship.following || relationship.requested)) {
          setIsActionInProgress(true);
          dispatch(unfollowAccount(accountId));
          setTimeout(() => setIsActionInProgress(false), 1000);
        } else {
          setIsActionInProgress(true);
          dispatch(followAccount(accountId));
          setTimeout(() => setIsActionInProgress(false), 1000);
        }
      } finally {
        setTimeout(() => {
          isProcessingRef.current = false;
          lastEventTypeRef.current = null;
        }, 100);
        
        setTimeout(() => {
          dispatch(fetchMostSuggestions());
        }, 1000);
      }
    },
    [dispatch, accountId, relationship, account, signedIn, isActionInProgress]
  );

  let label;

  if (!signedIn) {
    label = intl.formatMessage(messages.follow);
  } else if (accountId === me) {
    label = intl.formatMessage(messages.edit_profile);
  } else if (onlyFollow) {
    if (!relationship?.following && !relationship?.requested) {
      label = intl.formatMessage(messages.follow);
    }
  } else if (!relationship) {
    label = <LoadingIndicator />;
  } else if (relationship.following && relationship.followed_by) {
    label = intl.formatMessage(messages.mutual);
  } else if (relationship.following || relationship.requested) {
    label = intl.formatMessage(messages.unfollow);
  } else if (relationship.followed_by) {
    label = intl.formatMessage(messages.followBack);
  } else {
    label = intl.formatMessage(messages.follow);
  }

  const handleOpenProfile = useCallback(async () => {
    const token = await fetchLaoIdToken();
    if (token) {
      dispatch(openModal({
        modalType: 'LAOID_PROFILE',
        modalProps: {
          accessToken: token,
          accountId: accountId,
        },
      }));
    }
  }, [dispatch, accountId]);

  if (
    onlyFollow &&
    relationship !== undefined &&
    (relationship.following || relationship.requested)
  ) {
    return null;
  }
  
  return (
    <>
      {onlyFollow && <span className='dot-separator-tiny'>•</span>}
      <Button
        onClick={handleClick}
        disabled={
          isActionInProgress ||
          relationship?.blocked_by ||
          relationship?.blocking ||
          (!(relationship?.following || relationship?.requested) &&
            (account?.suspended || !!account?.moved))
        }
        secondary={following}
        compact={compact}
        className={following ? 'button--destructive' : undefined}
      >
        {isActionInProgress ? <LoadingIndicator /> : label}
      </Button>
    </>
  );
};
